import './ClientDataMapping.scss';
import cn from 'classnames';
import { singularize } from 'inflected';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { MdAdd } from 'react-icons/md';
import { IsoString } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { Button } from '@Components/Button';
import { Background, Design, Size } from '@Components/designConstants';
import { InfoTooltip } from '@Components/InfoTooltip/InfoTooltip';
import { Page } from '@Components/Page';
import { SegmentedControlButton } from '@Components/SegmentedControlButton/SegmentedControlButton/SegmentedControlButton';
import { Spinner } from '@Components/Spinner';
import { useAppSelector } from '../../redux';
import { useSetError } from '../../redux/modals';
import { emailSelector, isAdminSelector } from '../../redux/user';
import { conditionallyPluralizeWithCount } from '../../utils/text-utils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useTabbedNav } from '../../utils/hooks/useNav';
import { LocationType } from '../../utils/routing/location-constants';
import { AddClientsModal } from './AddClientsModal';
import { AddMappingModal } from './AddMappingModal/AddMappingModal';
import { BulkCompanyPickerModal } from './BulkCompanyPickerModal';
import {
  assignMappingItems,
  fetchClientMappings,
  fetchGlobalMappings,
  reassignMappingItems,
  unassignMappingItems
} from './client-data-mapping-api';
import {
  AssignMappingItem,
  buildAssignMappingItem,
  GENERIC_CID,
  GroupedMappingRow,
  groupMappingRows,
  MAPPING_TABS,
  MappingItemType,
  MappingPartition,
  MappingPartitionKey,
  MappingRow,
  MappingTabConfig,
  toUnassignedShape,
  UnassignedMappingRow
} from './client-data-mapping-constants';
import { isReadOnlyTab } from './client-data-mapping-utils';
import { AI_BLACKLIST_TAB_KEY, AiBlacklistTab } from './AiBlacklistTab';
import { CompanyPickerModal } from './CompanyPickerModal';
import { MappingTable } from './MappingTable/MappingTable';

interface ClientDataMappingProps {
  scope: LocationType;
}

// Input shape for handleReassignSubmit -- callers pull (itemId, fromCid) out of their own row
// shape at the boundary so the handler stays uniform across single (MappingRow → cid) and bulk
// (GroupedMappingRow → cids[0]) sources.
interface ReassignInput {
  row: UnassignedMappingRow;
  fromCid: string;
}

type PageTab = MappingItemType | typeof AI_BLACKLIST_TAB_KEY;

const MAPPING_NAVS = MAPPING_TABS.map(t => ({ label: t.label, key: t.key }));
const AI_BLACKLIST_NAV = { label: 'AI Blacklist', key: AI_BLACKLIST_TAB_KEY };
// Precomputed once (module scope, not per-render) since they only depend on scope, not any
// component state -- keeps a stable array identity into useTabbedNav across re-renders.
const GLOBAL_NAVS = [...MAPPING_NAVS, AI_BLACKLIST_NAV];
const GLOBAL_NAV_KEYS = GLOBAL_NAVS.map(n => n.key) as PageTab[];
const CLIENT_NAV_KEYS = MAPPING_NAVS.map(n => n.key) as PageTab[];

const ClientDataMappingBase = ({ scope }: ClientDataMappingProps) => {
  // Only meaningful for scope === LocationType.Client -- the global page has no single company
  // context.
  const { cid } = useCompanyInfo();
  const setError = useSetError();
  const currentUserEmail = useAppSelector(emailSelector);

  const isGlobal = scope === LocationType.Global;
  const pageNavs = isGlobal ? GLOBAL_NAVS : MAPPING_NAVS;
  const pageNavKeys = isGlobal ? GLOBAL_NAV_KEYS : CLIENT_NAV_KEYS;

  const { tab, goToTab } = useTabbedNav<PageTab>({
    baseURL: isGlobal ? 'client-data-mapping-admin' : 'client-data-mapping',
    defaultKey: MAPPING_TABS[0].key,
    navs: pageNavKeys
  });

  const isAiBlacklistTab = tab === AI_BLACKLIST_TAB_KEY;
  const currentTab = MAPPING_TABS.find(t => t.key === tab);

  const [assignedByType, setAssignedByType] = useState<
    Partial<Record<MappingItemType, MappingRow[]>>
  >({});
  // Unassigned rows are only populated for the Global scope. Client scope has no notion of
  // per-company unassigned, so this stays empty there.
  const [unassignedByType, setUnassignedByType] = useState<
    Partial<Record<MappingItemType, UnassignedMappingRow[]>>
  >({});
  // Generic (public/shared, cid === GENERIC_CID) rows -- only populated for item types whose
  // tab config sets allowGeneric (slack_channel and email_domain). Global scope only.
  const [genericByType, setGenericByType] = useState<
    Partial<Record<MappingItemType, MappingRow[]>>
  >({});
  // Per-tab load bookkeeping. loadedTabs = has ever completed a successful fetch (so the tab
  // has data to show); loadingTabs = currently has a fetch in flight (drives the table's own
  // empty-state spinner during mutation refreshes). The distinction matters because we
  // prefetch every tab on landing -- Page.isLoading only cares whether the *current* tab
  // has data yet, so page-level telemetry fires once per landing rather than per tab switch.
  const [loadedTabs, setLoadedTabs] = useState<Set<MappingItemType>>(new Set());
  const [loadingTabs, setLoadingTabs] = useState<Set<MappingItemType>>(new Set());
  // Global-scope sub-tab within each type-tab. Defaults to Unassigned since global admins
  // typically land here to triage the unassigned pile; Assigned is the drill-down after that.
  // Persists across type switches so toggling once doesn't have to be repeated per tab.
  const [partition, setPartition] = useState<MappingPartitionKey>(MappingPartition.Unassigned);

  const [showAddModal, setShowAddModal] = useState(false);
  const [reassignRow, setReassignRow] = useState<MappingRow | null>(null);
  const [assignRow, setAssignRow] = useState<UnassignedMappingRow | null>(null);
  // Row to open AddClientsModal against -- the grouped row being extended with additional
  // client assignments. Distinct from reassignRow: this is purely additive (existing cids
  // stay), triggered from the "Add additional clients" per-row action on Assigned rows.
  const [addClientsRow, setAddClientsRow] = useState<GroupedMappingRow | null>(null);
  // Selection buckets for the bulk-picker modal. Only one is non-null at a time -- the
  // MappingTable's checkbox column and additionalControls buttons drive these. Rows are
  // captured at click time (a rendering refetch after mutation invalidates ids, so we snapshot
  // rather than re-derive from state).
  const [bulkAssignRows, setBulkAssignRows] = useState<UnassignedMappingRow[] | null>(null);
  const [bulkReassignRows, setBulkReassignRows] = useState<GroupedMappingRow[] | null>(null);

  // Grouped row shape: consolidates the N (item, cid) MappingRow entries the API returns for a
  // domain into a single row keyed by itemId, with cids[] carrying every client that owns the
  // item. Memoized per-tab so a stable `assignedByType[tab.key]` reference means a stable
  // grouped output -- MappingTable's useMemo(headers) doesn't thrash. Generic gets the same
  // treatment defensively; in practice at most one active null-cid row exists per item, so
  // grouping is a no-op there.
  const groupedAssignedByType = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(assignedByType).map(([key, rows]) => [key, groupMappingRows(rows ?? [])])
      ) as Partial<Record<MappingItemType, GroupedMappingRow[]>>,
    [assignedByType]
  );
  const groupedGenericByType = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(genericByType).map(([key, rows]) => [key, groupMappingRows(rows ?? [])])
      ) as Partial<Record<MappingItemType, GroupedMappingRow[]>>,
    [genericByType]
  );

  // Adapter -- MappingTable's onReassign callback receives a GroupedMappingRow, but the
  // CompanyPickerModal takes a single-cid MappingRow (its "fromCid" is unambiguous). Only
  // rendered from the MappingTable when the grouped row has exactly one cid, so this is a
  // straightforward degrade; if a caller ever hands us a multi-cid grouped row, silently
  // bail rather than pretend to pick one.
  const openReassignForGroupedRow = useCallback((row: GroupedMappingRow) => {
    if (row.cids.length !== 1) return;
    setReassignRow({
      itemId: row.itemId,
      itemType: row.itemType,
      externalId: row.externalId,
      displayName: row.displayName,
      callDate: row.callDate,
      source: row.source,
      syncedAt: row.syncedAt,
      cid: row.cids[0],
      isManual: row.isManual,
      lastModifiedBy: row.lastModifiedBy,
      lastModified: row.lastModified,
      unassignedAt: null
    });
  }, []);

  const loadTab = useCallback(
    async (itemType: MappingItemType) => {
      setLoadingTabs(prev => new Set(prev).add(itemType));
      try {
        if (scope === LocationType.Client) {
          const rows = await fetchClientMappings(cid, itemType);
          setAssignedByType(prev => ({ ...prev, [itemType]: rows }));
        } else {
          const { assigned, unassigned, generic } = await fetchGlobalMappings(itemType);
          setAssignedByType(prev => ({ ...prev, [itemType]: assigned }));
          setUnassignedByType(prev => ({ ...prev, [itemType]: unassigned }));
          setGenericByType(prev => ({ ...prev, [itemType]: generic }));
        }
        setLoadedTabs(prev => new Set(prev).add(itemType));
      } catch (e) {
        setError({ message: e.message, reportError: e });
      } finally {
        setLoadingTabs(prev => {
          const next = new Set(prev);
          next.delete(itemType);
          return next;
        });
      }
    },
    [scope, cid, setError]
  );

  // On the Global scope, cid is irrelevant (global fetches aren't scoped to a company).
  // Pinning to undefined prevents a cid change (undefined → actual cid from useCompanyInfo)
  // from triggering a redundant second batch of prefetches on the Global page.
  const tabLoadCid = scope === LocationType.Client ? cid : undefined;
  useEffect(() => {
    if (scope === LocationType.Client && !tabLoadCid) return;
    // Prefetch every tab on landing (and on cid/scope change). The current tab blocks the
    // page's initial spinner via `Page.isLoading` below; the rest load in the background
    // so switching to them is instant. Reset the caches first since cid/scope changed.
    setAssignedByType({});
    setUnassignedByType({});
    setGenericByType({});
    setLoadedTabs(new Set());
    MAPPING_TABS.forEach(t => loadTab(t.key));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabLoadCid, scope]);

  // Optimistic mutation helper. Callers pass an `apply` closure that runs the state changes
  // synchronously (via the segment setState fns), plus an `apiCall` closure that returns the
  // API's per-item results. Semantics:
  //   - Snapshot the three by-type maps *before* apply() runs, so a partial or full API
  //     failure can revert to the exact pre-mutation state.
  //   - Fire apiCall() in the background. On any failure (thrown or `success: false`),
  //     revert the snapshot and surface the error. On success, do nothing further -- the
  //     optimistic delta is our source of truth until the next loadTab() (which may run for
  //     unrelated reasons, e.g. tab switch).
  //   - Purposely not calling loadTab() on success: the point of the optimistic path is to
  //     skip the refetch round-trip. If the optimistic delta ever diverges from the server's
  //     real outcome (edge case), the next natural refetch will reconcile.
  const optimisticMutate = useCallback(
    async (
      itemType: MappingItemType,
      apply: () => void,
      apiCall: () => Promise<{ success: boolean; message?: string }[]>,
      failureLabel: string
    ) => {
      const assignedSnapshot = assignedByType[itemType];
      const unassignedSnapshot = unassignedByType[itemType];
      const genericSnapshot = genericByType[itemType];
      apply();
      const revert = () => {
        setAssignedByType(prev => ({ ...prev, [itemType]: assignedSnapshot ?? [] }));
        setUnassignedByType(prev => ({ ...prev, [itemType]: unassignedSnapshot ?? [] }));
        setGenericByType(prev => ({ ...prev, [itemType]: genericSnapshot ?? [] }));
      };
      try {
        const results = await apiCall();
        const failed = results.filter(r => !r.success);
        if (failed.length) {
          revert();
          setError({
            message: `${failureLabel}: ${
              failed
                .map(f => f.message)
                .filter(Boolean)
                .join('; ') || 'unknown error'
            }`
          });
          // Force a refetch after revert to reconcile with the true server state -- the
          // partial-failure case can leave the server in a state that doesn't match either
          // our optimistic delta or the pre-mutation snapshot.
          loadTab(itemType);
        }
      } catch (e) {
        revert();
        setError({ message: e.message, reportError: e });
        loadTab(itemType);
      }
    },
    [assignedByType, unassignedByType, genericByType, loadTab, setError]
  );

  // Synthesize a MappingRow-shaped object from an UnassignedMappingRow when applying an
  // optimistic assign delta. The lastModified fields aren't known until the server writes
  // the audit row -- best-effort with the current user + wall clock so the row renders
  // sensibly in the interim; on the next real refetch these get overwritten with the truth.
  const synthesizeMappingRow = useCallback(
    (base: UnassignedMappingRow, targetCid: string, isManual = true): MappingRow => ({
      ...base,
      cid: targetCid,
      isManual,
      lastModifiedBy: currentUserEmail || null,
      lastModified: new Date().toISOString() as IsoString,
      unassignedAt: null
    }),
    [currentUserEmail]
  );

  // Bound to the currently-selected tab (recomputes only when the tab or its data-loaders
  // change), so it can be passed directly to MappingTable as a stable callback instead of a
  // fresh inline arrow every render.
  //
  // Grouped-row input: each row can carry multiple cids, so we fan out to one unassign call
  // per (cid) group, mirroring how the API's per-call `company` param works. A bulk selection
  // across N domains × M cids ends up as at most (unique cids) API calls.
  const handleRemove = useCallback(
    async (rows: GroupedMappingRow[]) => {
      if (!currentTab) return;
      const itemType = currentTab.key;
      const rowsByCid = new Map<string, number[]>();
      let totalPairings = 0;
      rows.forEach(row => {
        row.cids.forEach(pairCid => {
          rowsByCid.set(pairCid, [...(rowsByCid.get(pairCid) ?? []), row.itemId]);
          totalPairings += 1;
        });
      });
      const isGenericSegment = partition === MappingPartition.Generic;
      const affectedItemIds = new Set(rows.map(r => r.itemId));
      // Optimistic delta: strip the (item, cid) pairings from their current segment. Every
      // affected item that has no remaining pairings in that segment migrates to Unassigned.
      const apply = () => {
        if (isGenericSegment) {
          setGenericByType(prev => {
            const existing = prev[itemType] ?? [];
            return { ...prev, [itemType]: existing.filter(r => !affectedItemIds.has(r.itemId)) };
          });
        } else {
          setAssignedByType(prev => {
            const existing = prev[itemType] ?? [];
            // Drop the exact (itemId, cid) pairings the caller asked to remove; leave any
            // other pairings for the same itemId in place (multi-cid rows where the user
            // hit "remove all" clear them all; single-cid rows collapse fully).
            const cidsToRemoveByItem = new Map<number, Set<string>>();
            rows.forEach(row => {
              cidsToRemoveByItem.set(row.itemId, new Set(row.cids));
            });
            return {
              ...prev,
              [itemType]: existing.filter(r => !cidsToRemoveByItem.get(r.itemId)?.has(r.cid))
            };
          });
        }
        // Every affected item lands in Unassigned so long as the caller's rows contained the
        // full cid set for that item (which is how the UI's grouped Remove always works).
        setUnassignedByType(prev => {
          const existing = prev[itemType] ?? [];
          const already = new Set(existing.map(r => r.itemId));
          const additions = rows.filter(r => !already.has(r.itemId)).map(toUnassignedShape);
          return { ...prev, [itemType]: [...existing, ...additions] };
        });
      };
      await optimisticMutate(
        itemType,
        apply,
        async () => {
          const resultGroups = await Promise.all(
            [...rowsByCid.entries()].map(([company, itemIds]) =>
              unassignMappingItems(company, itemType, itemIds)
            )
          );
          return resultGroups.flat();
        },
        `Failed to unassign ${totalPairings} mapping${totalPairings === 1 ? '' : 's'}`
      );
    },
    [currentTab, partition, optimisticMutate]
  );

  const refreshCurrentTab = useCallback(() => {
    if (currentTab) {
      loadTab(currentTab.key);
    }
  }, [currentTab, loadTab]);

  // Per-row "Mark as generic" on a grouped Assigned row. Two shapes:
  //   - Single-CID row (cids.length === 1): reassign(fromCid=cids[0], toCid=GENERIC_CID). One
  //     atomic transaction on the backend; the mutex is never in play.
  //   - Multi-CID row (cids.length > 1): the backend mutex blocks direct reassign to generic
  //     while any real-cid assignment exists. Sequence the transition as
  //     N unassigns (fan out over cids) → 1 assign(GENERIC_CID). Not one atomic transaction --
  //     each unassign and the final assign each land as their own audit event -- but every
  //     step is safe to retry, so a partial failure surfaces as an error and the tab refresh
  //     shows the true state.
  const handleMarkGeneric = useCallback(
    async (row: GroupedMappingRow) => {
      const { itemType } = row;
      // Optimistic delta: drop every (itemId, cid) row from Assigned, add a single
      // (itemId, GENERIC_CID) row to Generic. The synthesized generic row uses the current
      // user + wall clock for last-modified until the next real refetch.
      const apply = () => {
        setAssignedByType(prev => {
          const existing = prev[itemType] ?? [];
          return {
            ...prev,
            [itemType]: existing.filter(r => r.itemId !== row.itemId)
          };
        });
        setGenericByType(prev => {
          const existing = prev[itemType] ?? [];
          const synthesized = synthesizeMappingRow(
            {
              itemId: row.itemId,
              itemType: row.itemType,
              externalId: row.externalId,
              displayName: row.displayName,
              callDate: row.callDate,
              source: row.source,
              syncedAt: row.syncedAt
            },
            GENERIC_CID
          );
          return { ...prev, [itemType]: [...existing, synthesized] };
        });
      };
      await optimisticMutate(
        itemType,
        apply,
        async () => {
          if (row.cids.length === 1) {
            return reassignMappingItems(itemType, [
              { itemId: row.itemId, fromCid: row.cids[0], toCid: GENERIC_CID }
            ]);
          }
          // Multi-CID: unassign each client first, then assign generic. Each step is safe to
          // retry (the mutations are idempotent). If any unassign fails, the trailing
          // assign(GENERIC_CID) still fires; optimisticMutate reverts on partial failure by
          // triggering a refetch anyway.
          const unassignResults = await Promise.all(
            row.cids.map(fromCid => unassignMappingItems(fromCid, itemType, [row.itemId]))
          );
          const assignResults = await assignMappingItems(GENERIC_CID, itemType, [
            buildAssignMappingItem(row)
          ]);
          return [...unassignResults.flat(), ...assignResults];
        },
        'Failed to mark as generic'
      );
    },
    [optimisticMutate, synthesizeMappingRow]
  );

  // ─── Picker-modal submit handlers ─────────────────────────────────────────
  //
  // These wrap the API calls the picker modals used to fire themselves inside optimisticMutate,
  // so the row deltas apply synchronously and the modal can close immediately.
  //
  // Layout mirrors handleMarkGeneric above: apply() moves rows between partitions locally,
  // apiCall() replays the modal's original API-call sequence. Modals just hand back
  // `targetCids` and close; no await from their side.

  // Split targetCids into the buckets we might write to. GENERIC_CID rows go to genericByType;
  // real cids to assignedByType.
  const partitionAdditions = useCallback(
    (row: UnassignedMappingRow, targetCids: string[]) => {
      const additions = targetCids.map(targetCid => synthesizeMappingRow(row, targetCid));
      return {
        assigned: additions.filter(r => r.cid !== GENERIC_CID),
        generic: additions.filter(r => r.cid === GENERIC_CID)
      };
    },
    [synthesizeMappingRow]
  );

  // Unified 1..N assign handler -- callers wrap a single row as `[row]` at the call site (see
  // the single-row modal render below). Same closure whether the user assigned one row via
  // CompanyPickerModal or 20 via BulkCompanyPickerModal; behaviour differs only in the count of
  // affected rows.
  const handleAssignSubmit = useCallback(
    async (rows: UnassignedMappingRow[], targetCids: string[]) => {
      if (rows.length === 0) return;
      const { itemType } = rows[0];
      const affectedItemIds = new Set(rows.map(r => r.itemId));
      const perRowAdditions = rows.map(row => partitionAdditions(row, targetCids));
      const assignedAdds = perRowAdditions.flatMap(a => a.assigned);
      const genericAdds = perRowAdditions.flatMap(a => a.generic);
      const apply = () => {
        setUnassignedByType(prev => ({
          ...prev,
          [itemType]: (prev[itemType] ?? []).filter(r => !affectedItemIds.has(r.itemId))
        }));
        if (assignedAdds.length) {
          setAssignedByType(prev => ({
            ...prev,
            [itemType]: [...(prev[itemType] ?? []), ...assignedAdds]
          }));
        }
        if (genericAdds.length) {
          setGenericByType(prev => ({
            ...prev,
            [itemType]: [...(prev[itemType] ?? []), ...genericAdds]
          }));
        }
      };
      const assignItems = rows.map(buildAssignMappingItem);
      await optimisticMutate(
        itemType,
        apply,
        async () => {
          const results = await Promise.all(
            targetCids.map(targetCid => assignMappingItems(targetCid, itemType, assignItems))
          );
          return results.flat();
        },
        `Failed to assign ${conditionallyPluralizeWithCount(rows.length, 'mapping')}`
      );
    },
    [optimisticMutate, partitionAdditions]
  );

  // Unified 1..N reassign handler. Callers hand back `{ row, fromCid }` per input so the
  // single-row (MappingRow) and bulk (GroupedMappingRow) modal call sites can each normalize
  // into the same shape at the boundary -- MappingRow.cid vs. GroupedMappingRow.cids[0].
  const handleReassignSubmit = useCallback(
    async (inputs: ReassignInput[], targetCids: string[]) => {
      if (inputs.length === 0) return;
      const { itemType } = inputs[0].row;
      // Reassign wipes each specific (itemId, fromCid) pair from whichever bucket it currently
      // lives in (Assigned or Generic), then fans out the new targets into their buckets.
      const fromCidByItemId = new Map(inputs.map(({ row, fromCid }) => [row.itemId, fromCid]));
      const dropSources = (mappingRows: MappingRow[]) =>
        mappingRows.filter(r => fromCidByItemId.get(r.itemId) !== r.cid);
      const perRowAdditions = inputs.map(({ row }) => partitionAdditions(row, targetCids));
      const assignedAdds = perRowAdditions.flatMap(a => a.assigned);
      const genericAdds = perRowAdditions.flatMap(a => a.generic);
      const apply = () => {
        setAssignedByType(prev => ({
          ...prev,
          [itemType]: [...dropSources(prev[itemType] ?? []), ...assignedAdds]
        }));
        setGenericByType(prev => ({
          ...prev,
          [itemType]: [...dropSources(prev[itemType] ?? []), ...genericAdds]
        }));
      };
      const assignItems = inputs.map(({ row }) => buildAssignMappingItem(row));
      await optimisticMutate(
        itemType,
        apply,
        async () => {
          const [firstTarget, ...restTargets] = targetCids;
          // Preserve the modal's original ordering: the first target replaces via /reassign
          // (so a modifiedSince consumer sees one reassign event, not an unassign+assign
          // pair); any additional targets ride on follow-on /assign calls.
          const reassignPayload = inputs.map(({ row, fromCid }) => ({
            itemId: row.itemId,
            fromCid,
            toCid: firstTarget
          }));
          const reassignResults = await reassignMappingItems(itemType, reassignPayload);
          const restResults = await Promise.all(
            restTargets.map(targetCid => assignMappingItems(targetCid, itemType, assignItems))
          );
          return [...reassignResults, ...restResults.flat()];
        },
        `Failed to reassign ${conditionallyPluralizeWithCount(inputs.length, 'mapping')}`
      );
    },
    [optimisticMutate, partitionAdditions]
  );

  const handleAddClientsSubmit = useCallback(
    async (row: GroupedMappingRow, targetCids: string[]) => {
      const { itemType } = row;
      // Purely additive -- existing (itemId, cid) pairs stay; new cids get their own MappingRow
      // entries under the same itemId. groupMappingRows collapses them into the same visual row.
      const { assigned: assignedAdds, generic: genericAdds } = partitionAdditions(
        toUnassignedShape(row),
        targetCids
      );
      const apply = () => {
        if (assignedAdds.length) {
          setAssignedByType(prev => ({
            ...prev,
            [itemType]: [...(prev[itemType] ?? []), ...assignedAdds]
          }));
        }
        if (genericAdds.length) {
          setGenericByType(prev => ({
            ...prev,
            [itemType]: [...(prev[itemType] ?? []), ...genericAdds]
          }));
        }
      };
      const assignItem = buildAssignMappingItem(row);
      await optimisticMutate(
        itemType,
        apply,
        async () => {
          const results = await Promise.all(
            targetCids.map(targetCid => assignMappingItems(targetCid, itemType, [assignItem]))
          );
          return results.flat();
        },
        `Failed to add clients to ${row.displayName}`
      );
    },
    [optimisticMutate, partitionAdditions]
  );

  // "Mark as generic" directly from the Unassigned partition. Moves the item from unassigned to
  // generic by assigning it to GENERIC_CID. Handles both single-row (called with [row]) and
  // bulk (called with the full selection) via the same signature.
  const handleMarkGenericFromUnassigned = useCallback(
    async (rows: UnassignedMappingRow[]) => {
      if (rows.length === 0) return;
      const { itemType } = rows[0];
      const affectedItemIds = new Set(rows.map(r => r.itemId));
      const apply = () => {
        setUnassignedByType(prev => ({
          ...prev,
          [itemType]: (prev[itemType] ?? []).filter(r => !affectedItemIds.has(r.itemId))
        }));
        setGenericByType(prev => ({
          ...prev,
          [itemType]: [
            ...(prev[itemType] ?? []),
            ...rows.map(row => synthesizeMappingRow(row, GENERIC_CID))
          ]
        }));
      };
      await optimisticMutate(
        itemType,
        apply,
        () => assignMappingItems(GENERIC_CID, itemType, rows.map(buildAssignMappingItem)),
        `Failed to mark ${conditionallyPluralizeWithCount(rows.length, 'item')} as generic`
      );
    },
    [optimisticMutate, synthesizeMappingRow]
  );

  // "Confirm" oracle-suggested assigned rows: re-assigns each item to the same company via
  // POST /mappings/assign, which hits the ON CONFLICT DO UPDATE path and flips is_manual = true.
  // Filters down to confirmable rows (oracle-sourced, not yet manual) so callers can pass an
  // arbitrary selection without pre-filtering. Per-row button calls onConfirm([row]); the bulk
  // control calls onConfirm(confirmableSelected).
  // Optimistic: immediately reflect isManual = true on local rows; revert on failure.
  const handleConfirm = useCallback(
    async (rows: GroupedMappingRow[]) => {
      const confirmable = rows.filter(r => r.source === 'oracle' && !r.isManual);
      if (confirmable.length === 0) return;
      const { itemType } = confirmable[0];
      const confirmedIds = new Set(confirmable.map(r => r.itemId));
      const now = new Date().toISOString() as IsoString;
      const apply = () => {
        setAssignedByType(prev => ({
          ...prev,
          [itemType]: (prev[itemType] ?? []).map(r =>
            confirmedIds.has(r.itemId)
              ? {
                  ...r,
                  isManual: true,
                  lastModifiedBy: currentUserEmail || null,
                  lastModified: now
                }
              : r
          )
        }));
      };
      // Group items by cid — each confirmable row may be assigned to multiple clients,
      // and assignMappingItems takes one company at a time.
      const byCid = new Map<string, AssignMappingItem[]>();
      for (const row of confirmable) {
        const item = buildAssignMappingItem(row);
        for (const c of row.cids) {
          const existing = byCid.get(c) ?? [];
          existing.push(item);
          byCid.set(c, existing);
        }
      }
      await optimisticMutate(
        itemType,
        apply,
        () =>
          Promise.all(
            Array.from(byCid.entries()).map(([c, items]) => assignMappingItems(c, itemType, items))
          ).then(r => r.flat()),
        confirmable.length === 1
          ? `Failed to confirm ${confirmable[0].displayName}`
          : `Failed to confirm ${confirmable.length} items`
      );
    },
    [currentUserEmail, optimisticMutate]
  );

  // Synchronous duplicate check for the AddMappingModal -- covers rows in all three loaded
  // partitions (unassigned, assigned, generic). Slack-only: other types handle duplicates via
  // ON CONFLICT upsert on the backend and don't need a frontend guard.
  const checkSlackExternalIdExists = useCallback(
    (externalId: string) =>
      (unassignedByType.slack_channel ?? []).some(r => r.externalId === externalId) ||
      (assignedByType.slack_channel ?? []).some(r => r.externalId === externalId) ||
      (genericByType.slack_channel ?? []).some(r => r.externalId === externalId),
    [unassignedByType, assignedByType, genericByType]
  );

  // Page.isLoading is telemetry-only (fires MxE.PageDataLoad on the true->false edge). By
  // gating on "current tab not yet loaded" rather than the raw fetch state, telemetry fires
  // once per landing (when the initially-viewed tab's data arrives), not per tab switch --
  // background prefetches for the other tabs don't re-fire it.
  const currentTabLoaded = Boolean(currentTab && loadedTabs.has(currentTab.key));
  const currentTabLoading = Boolean(currentTab && loadingTabs.has(currentTab.key));

  return (
    <Page
      pageType='Client Data Mapping'
      title='Client Data Mapping'
      navs={pageNavs}
      selectedNav={tab}
      onNav={key => goToTab(key as PageTab)}
      isLoading={isAiBlacklistTab ? false : !currentTabLoaded}
    >
      {isAiBlacklistTab ? (
        <AiBlacklistTab />
      ) : (
        <ClientDataMappingBody
          scope={scope}
          currentTab={currentTab}
          loading={currentTabLoading}
          currentTabLoaded={currentTabLoaded}
          assignedByType={groupedAssignedByType}
          unassignedByType={unassignedByType}
          genericByType={groupedGenericByType}
          partition={partition}
          onPartitionChange={setPartition}
          onAdd={() => setShowAddModal(true)}
          onRemove={handleRemove}
          onReassign={openReassignForGroupedRow}
          onMarkGeneric={handleMarkGeneric}
          onAddClients={setAddClientsRow}
          onAssign={setAssignRow}
          onMarkGenericFromUnassigned={handleMarkGenericFromUnassigned}
          onBulkAssign={setBulkAssignRows}
          onBulkReassign={setBulkReassignRows}
          onConfirm={handleConfirm}
        />
      )}
      {currentTab && (
        <>
          {showAddModal && (
            <AddMappingModal
              tab={currentTab}
              fixedCid={scope === LocationType.Client ? cid : undefined}
              onHide={() => setShowAddModal(false)}
              onAdded={refreshCurrentTab}
              existsCheck={
                currentTab.key === 'slack_channel' ? checkSlackExternalIdExists : undefined
              }
            />
          )}
          {reassignRow && (
            <CompanyPickerModal
              tab={currentTab}
              row={reassignRow}
              onHide={() => setReassignRow(null)}
              onSubmit={targetCids =>
                handleReassignSubmit([{ row: reassignRow, fromCid: reassignRow.cid }], targetCids)
              }
            />
          )}
          {assignRow && (
            <CompanyPickerModal
              tab={currentTab}
              row={assignRow}
              onHide={() => setAssignRow(null)}
              onSubmit={targetCids => handleAssignSubmit([assignRow], targetCids)}
            />
          )}
          {addClientsRow && (
            <AddClientsModal
              tab={currentTab}
              row={addClientsRow}
              onHide={() => setAddClientsRow(null)}
              onSubmit={targetCids => handleAddClientsSubmit(addClientsRow, targetCids)}
            />
          )}
          {bulkAssignRows && (
            <BulkCompanyPickerModal
              tab={currentTab}
              mode='assign'
              rows={bulkAssignRows}
              onHide={() => setBulkAssignRows(null)}
              onSubmit={targetCids => handleAssignSubmit(bulkAssignRows, targetCids)}
            />
          )}
          {bulkReassignRows && (
            <BulkCompanyPickerModal
              tab={currentTab}
              mode='reassign'
              rows={bulkReassignRows}
              onHide={() => setBulkReassignRows(null)}
              onSubmit={targetCids =>
                handleReassignSubmit(
                  bulkReassignRows.map(row => ({
                    row: toUnassignedShape(row),
                    fromCid: row.cids[0]
                  })),
                  targetCids
                )
              }
            />
          )}
        </>
      )}
    </Page>
  );
};

interface ClientDataMappingBodyProps {
  scope: LocationType;
  currentTab: MappingTabConfig | undefined;
  // Currently-viewed tab has a fetch in flight (drives the table's own empty-state spinner
  // during mutation refreshes).
  loading: boolean;
  // Currently-viewed tab has ever completed a successful fetch (has data to render). Gates the
  // page-level initial spinner without spinning again on subsequent tab switches that hit
  // already-prefetched data.
  currentTabLoaded: boolean;
  assignedByType: Partial<Record<MappingItemType, GroupedMappingRow[]>>;
  unassignedByType: Partial<Record<MappingItemType, UnassignedMappingRow[]>>;
  genericByType: Partial<Record<MappingItemType, GroupedMappingRow[]>>;
  partition: MappingPartitionKey;
  onPartitionChange: (partition: MappingPartitionKey) => void;
  onAdd: () => void;
  onRemove: (rows: GroupedMappingRow[]) => Promise<void>;
  onReassign: (row: GroupedMappingRow) => void;
  // Per-row shortcut on the Assigned segment for many-to-many item types -- opens
  // AddClientsModal so the user can attach additional clients to a row without touching
  // existing assignments.
  onAddClients: (row: GroupedMappingRow) => void;
  // Per-row shortcut on the Assigned segment for allowGeneric tabs -- unwinds the item's
  // client assignments and installs a generic one. See handleMarkGeneric in
  // ClientDataMappingBase.
  onMarkGeneric: (row: GroupedMappingRow) => Promise<void>;
  onAssign: (row: UnassignedMappingRow) => void;
  // Direct "mark as generic" from the Unassigned partition -- skips the picker modal and assigns
  // straight to GENERIC_CID. Only passed for allowGeneric tabs (slack_channel, email_domain).
  onMarkGenericFromUnassigned: (rows: UnassignedMappingRow[]) => Promise<void>;
  // Bulk selection handlers -- capture the checked rows so the parent can render the
  // BulkCompanyPickerModal against them. Snapshot at click time rather than derived state so a
  // mid-flow refetch doesn't invalidate the modal's row list.
  onBulkAssign: (rows: UnassignedMappingRow[]) => void;
  onBulkReassign: (rows: GroupedMappingRow[]) => void;
  // Confirms oracle-suggested rows as manually verified (sets is_manual). Filters the given
  // rows down to confirmable ones (oracle-sourced, not yet manual) internally.
  onConfirm?: (rows: GroupedMappingRow[]) => Promise<void>;
}

const ClientDataMappingBody = ({
  scope,
  currentTab,
  loading,
  currentTabLoaded,
  assignedByType,
  unassignedByType,
  genericByType,
  partition,
  onPartitionChange,
  onAdd,
  onRemove,
  onReassign,
  onMarkGeneric,
  onAddClients,
  onAssign,
  onMarkGenericFromUnassigned,
  onBulkAssign,
  onBulkReassign,
  onConfirm
}: ClientDataMappingBodyProps) => {
  const isAdmin = useAppSelector(isAdminSelector);
  // useTabbedNav guarantees a valid tab, so currentTab should always resolve -- guarded here
  // only to satisfy the (undefined) prop type and to short-circuit render before data loads.
  if (!currentTab) return null;

  const isGlobal = scope === LocationType.Global;
  const readOnly = isReadOnlyTab(currentTab, isAdmin);
  const currentAssigned = assignedByType[currentTab.key] ?? [];
  const currentUnassigned = unassignedByType[currentTab.key] ?? [];
  const currentGeneric = genericByType[currentTab.key] ?? [];
  // Generic segment collapses back to Assigned when the current tab doesn't allow it, so a
  // stale selection carried over from another tab can't wedge the view.
  const effectivePartition =
    partition === MappingPartition.Generic && !currentTab.allowGeneric
      ? MappingPartition.Assigned
      : partition;
  const showUnassignedView = isGlobal && effectivePartition === MappingPartition.Unassigned;
  const showGenericView = isGlobal && effectivePartition === MappingPartition.Generic;

  // Initial load for a tab -- no successful fetch yet. Subsequent reloads (after edits, or on
  // a tab switch that hits already-prefetched data) skip this and let the table's own
  // empty-state spinner show while any in-flight reload lands. The loading div is nested
  // inside .client-data-mapping-page so scoped SCSS rules apply in both states.
  const isInitialLoad = !currentTabLoaded;

  // Props every MappingTable variant carries -- spread into each branch below so the per-branch
  // JSX only names the fields that actually differ (partition + rows + handler set).
  const sharedTableProps = { tab: currentTab, scope, loading, readOnly };
  const buildMappingTable = (): ReactNode => {
    if (showUnassignedView) {
      return (
        <MappingTable
          key={`${currentTab.key}-unassigned`}
          {...sharedTableProps}
          partition={MappingPartition.Unassigned}
          rows={currentUnassigned}
          onAssign={onAssign}
          onBulkAssign={onBulkAssign}
          onMarkGeneric={currentTab.allowGeneric ? onMarkGenericFromUnassigned : undefined}
        />
      );
    }
    if (showGenericView) {
      return (
        <MappingTable
          key={`${currentTab.key}-generic`}
          {...sharedTableProps}
          partition={MappingPartition.Generic}
          rows={currentGeneric}
          onRemove={onRemove}
          onReassign={onReassign}
          onMarkGeneric={onMarkGeneric}
          onAddClients={onAddClients}
          onBulkReassign={onBulkReassign}
          onConfirm={onConfirm}
        />
      );
    }
    return (
      <MappingTable
        key={`${currentTab.key}-assigned`}
        {...sharedTableProps}
        partition={MappingPartition.Assigned}
        rows={currentAssigned}
        onRemove={onRemove}
        onReassign={onReassign}
        onMarkGeneric={onMarkGeneric}
        onAddClients={onAddClients}
        onBulkReassign={onBulkReassign}
        onConfirm={onConfirm}
      />
    );
  };
  const mappingTable = buildMappingTable();

  return (
    <div
      data-testid='client-data-mapping-page'
      className={cn('client-data-mapping-page', { 'is-global': isGlobal })}
    >
      {isInitialLoad && (
        <div className='client-data-mapping-loading'>
          <Spinner />
        </div>
      )}
      {!isInitialLoad && (
        <>
          <div className='client-data-mapping-toolbar'>
            {isGlobal && (
              <div data-testid='mapping-partition-toggle'>
                <SegmentedControlButton<MappingPartitionKey>
                  options={[
                    {
                      key: MappingPartition.Unassigned,
                      label: `Unassigned (${currentUnassigned.length})`
                    },
                    {
                      key: MappingPartition.Assigned,
                      label: `Assigned (${currentAssigned.length})`
                    },
                    ...(currentTab.allowGeneric
                      ? [
                          {
                            key: MappingPartition.Generic,
                            label: `Generic (${currentGeneric.length})`,
                            renderLabel: (label: string) => (
                              <span className='mapping-partition-generic-label'>
                                {label}
                                <InfoTooltip
                                  size={Size.MD}
                                  design={Design.NEUTRAL}
                                  background={
                                    effectivePartition === MappingPartition.Generic
                                      ? Background.DARK
                                      : Background.LIGHT
                                  }
                                >
                                  For categorizing {currentTab.label.toLowerCase()} affiliated with
                                  no specific client(s) — these should not be referenced by the
                                  Oracle Agent.
                                </InfoTooltip>
                              </span>
                            )
                          }
                        ]
                      : [])
                  ]}
                  selectedOption={effectivePartition}
                  onChange={onPartitionChange}
                />
              </div>
            )}
            <Button
              size={Size.LG}
              data-testid='add-mapping-button'
              leadingElement={<MdAdd />}
              onClick={onAdd}
              disabled={readOnly}
              tooltip={
                readOnly
                  ? {
                      text: `Only admins can add ${currentTab.label.toLowerCase()}. Ask an admin to add it for you.`
                    }
                  : undefined
              }
            >
              Add {singularize(currentTab.label)}
            </Button>
          </div>
          <div className='client-data-mapping-table'>{mappingTable}</div>
        </>
      )}
    </div>
  );
};

export const ClientDataMapping = () => <ClientDataMappingBase scope={LocationType.Client} />;
export const GlobalDataMapping = () => <ClientDataMappingBase scope={LocationType.Global} />;
